import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { IAssertPayload } from '@shared/asserts/i_assert_payload';
import { assert_is_safe_number, assert_true, assert_type_enum, not_implemented_type } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { z } from 'zod';
import { CONSTANTS__shared } from '../../../constants/CONSTANTS__shared';
import { ENUM__number_range } from './ENUM__number_range';

export class DTO__number_range extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.RANGE_OF_NUMBERS;

  static readonly ZERO = new DTO__number_range(0, 0);
  static readonly MINUS_ONE = new DTO__number_range(-1, -1);

  /*
   * STATIC METHODS
   */
  static generate_range_of_numbers(range: DTO__number_range, round_values: boolean = false): DTO__number_range {
    let [min, max] = [range.min, range.max];

    if (min > max) [min, max] = [max, min];

    let random_min = parseFloat((Math.random() * (max - min) + min).toFixed(2));
    let random_max = parseFloat((Math.random() * (max - random_min) + random_min).toFixed(2));

    if (round_values) [random_min, random_max] = [Math.round(random_min), Math.round(random_max)];

    return new DTO__number_range(random_min, random_max);
  }

  /*
   * INSTANCE FIELDS
   */
  type: ENUM__number_range;
  min: number;
  max: number;

  /*
   * METHODS
   */

  constructor(min: number = CONSTANTS__shared.NUMBER_EMPTY_VALUE, max: number = CONSTANTS__shared.NUMBER_EMPTY_VALUE) {
    super();

    this.type = ENUM__number_range.LOW_TO_HIGH;

    [this.min, this.max] = [min, max];
  }

  is_equal(other: DTO__number_range): boolean {
    return this.min == other.min && this.max == other.max;
  }

  get_random_value(precision: number = 0): number {
    let randomValue = this.min + Math.random() * this.get_delta();
    return randomValue.to_fixed(precision);
  }

  to_array(step: number = 1, precision: number = 0): number[] {
    const out = [];

    let delta_total = this.get_delta();

    const steps_count = Math.floor(delta_total / step);

    for (let i = 0; i <= steps_count; i++) {
      let delta_accumulated = i * step.to_fixed(precision);

      const value = this.min + delta_accumulated;
      out.push(value);
    }

    if (out[out.length - 1] !== this.max) {
      out.push(this.max.to_fixed(precision));
    }

    return out;
  }

  is_number_is_in_range(value: number): boolean {
    return this.min <= value && value <= this.max;
  }
  is_number_range_is_in_range(other: DTO__number_range): boolean {
    return this.min <= other.min && other.max <= this.max;
  }

  get_average(precision: number = 0): number {
    return ((this.max + this.min) / 2).to_fixed(precision);
  }

  get_percentage(percentage: number): number {
    return this.min + percentage * this.get_delta();
  }

  get_delta() {
    return this.max - this.min;
  }

  to_zod() {
    return z.number().min(this.min).max(this.max);
  }

  clamp(value: number) {
    return Math.min(this.max, Math.max(this.min, value));
  }

  clamp_range(range: DTO__number_range) {
    let min = this.clamp(range.min);
    let max = this.clamp(range.max);

    return new DTO__number_range(min, max);
  }

  create_inverted() {
    return new DTO__number_range(this.max, this.min);
  }

  value_to_percentage(value: number) {
    if (!this.is_number_is_in_range(value)) return NaN;

    const percent_per_step = 100 / this.get_delta();
    const percentage = percent_per_step * (value - this.min);

    let out = new DTO__number_range(0, 100).clamp(percentage);

    return out;
  }

  toString() {
    return JSON.stringify({ min: this.min, max: this.max });
  }

  /*
   * ASSERTS
   */

  assert_standard_fields(): void {
    assert_is_safe_number(this.min);
    assert_is_safe_number(this.max);

    assert_type_enum(this.type, ENUM__number_range);

    this.assert_min_max_by_type();
  }

  assert_min_max_by_type() {
    switch (this.type) {
      case ENUM__number_range.LOW_TO_HIGH:
        assert_true(this.min <= this.max);
        break;
      case ENUM__number_range.HIGH_TO_LOW:
        assert_true(this.min >= this.max);
        break;
      case ENUM__number_range.ANY:
        // do nothing
        break;
      default:
        not_implemented_type(this.type);
    }
  }

  assert_number_range_in_range(other: DTO__number_range, payload: IAssertPayload = {}) {
    assert_true(this.is_number_range_is_in_range(other), payload);
  }

  assert_number_in_range(value: number, payload: IAssertPayload = {}) {
    assert_is_safe_number(value);

    payload.assert_type ||= ENUM__error_type.NUMBER_NOT_IN_RANGE;

    assert_true(this.is_number_is_in_range(value), payload);
  }
}
